import { useState } from 'react'
import { dismissPersonaPrompt, updatePersona } from '@rpc/persona.rpc'
import { PERSONA_OPTIONS, type PersonaId } from '@shared/persona'

export function PersonaPromptCard(props: {
  onComplete?: (persona: PersonaId) => void
  onDismiss?: () => void
}) {
  const [selectedPersona, setSelectedPersona] = useState<PersonaId | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      await updatePersona({
        data: { persona: selectedPersona!, source: 'chat_prompt' },
      })
      props.onComplete?.(selectedPersona!)
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : 'Your role could not be saved. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const dismiss = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      await dismissPersonaPrompt()
      props.onDismiss?.()
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : 'The prompt could not be dismissed. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <aside className="rounded-2xl border border-blue-200 bg-blue-50/80 p-5 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/30">
      <h2 className="text-base font-semibold text-foreground">
        Help tailor BuildingRegs to your work
      </h2>
      <fieldset className="mt-3" disabled={isSubmitting}>
        <legend className="text-sm text-muted-foreground">
          What best describes your main role?
        </legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {PERSONA_OPTIONS.map((option) => (
            <label
              key={option.id}
              className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 dark:has-[:checked]:bg-blue-950/40"
            >
              <input
                type="radio"
                name="persona"
                value={option.id}
                checked={selectedPersona === option.id}
                onChange={() => setSelectedPersona(option.id)}
                required
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {error ? (
        <p className="mt-3 text-sm text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          className="min-h-11 rounded-full bg-blue-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectedPersona || isSubmitting}
          onClick={() => void save()}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="min-h-11 rounded-full border border-border bg-background px-5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSubmitting}
          onClick={() => void dismiss()}
        >
          Not now
        </button>
      </div>
    </aside>
  )
}
